import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { BACKEND_API_BASE_URL } from "utils/constants";
import {
  authenticatedFetch,
  AUTH_EXPIRED_EVENT,
  AUTH_TOKEN_EVENT,
} from "services/http";

const AuthContext = createContext(null);

// How long to wait before asking the backend who is signed in again, when it could not be reached.
const ME_RETRY_MS = 5000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem("access_token"));
  const [loading, setLoading] = useState(true);

  const clearLocalSession = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  }, []);

  // -> true when settled (signed in, or the session is over), false when the server could not be reached.
  const fetchCurrentUser = useCallback(async () => {
    try {
      const res = await authenticatedFetch("/api/auth/me/");
      const data = await res.json();
      if (data.id) setUser(data);
      else clearLocalSession();
      return true;
    } catch (error) {
      // Only a refused session (401, or 403 from a backend that answers a bad token that way) signs out. While the
      // backend is down (a deploy) keep the tokens and ask again.
      if (error.status === 401 || error.status === 403 || !localStorage.getItem("access_token")) {
        clearLocalSession();
        return true;
      }
      return false;
    }
  }, [clearLocalSession]);

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    let timer;
    const attempt = async () => {
      const settled = await fetchCurrentUser();
      if (cancelled) return;
      if (settled) setLoading(false);
      else timer = setTimeout(attempt, ME_RETRY_MS);
    };
    attempt();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [accessToken, fetchCurrentUser]);

  useEffect(() => {
    const onExpired = () => clearLocalSession();
    const onToken = (event) => setAccessToken(event.detail);
    // Another tab signed out (or in): follow it, rather than staying up with requests that all fail.
    const onStorage = (event) => {
      if (event.key !== "access_token" && event.key !== null) return;
      const token = localStorage.getItem("access_token");
      if (token) setAccessToken(token);
      else {
        setUser(null);
        setAccessToken(null);
      }
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    window.addEventListener(AUTH_TOKEN_EVENT, onToken);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
      window.removeEventListener(AUTH_TOKEN_EVENT, onToken);
      window.removeEventListener("storage", onStorage);
    };
  }, [clearLocalSession]);

  const login = async (identifier, password) => {
    const res = await fetch(`${BACKEND_API_BASE_URL}/api/auth/login/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identifier, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || "Login failed");
    localStorage.setItem("access_token", data.access);
    localStorage.setItem("refresh_token", data.refresh);
    setAccessToken(data.access);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await authenticatedFetch("/api/auth/logout/", { method: "POST" });
    } catch {}
    clearLocalSession();
  };

  return (
    <AuthContext.Provider value={{ user, accessToken, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
